import { debounceAsync, memoizeLastAsync } from '@src/utils/async';
import {
  DataConnectorRow,
  DataConnectorSource,
  DataConnectorSourceType
} from './data-connector-tool-constants';

// Source -> the substring its Auth Account (secret_path) must contain, mirroring the backend's
// own secret naming convention (production/<platform>/...).
const SOURCE_SECRET_SUBSTRING: Partial<Record<DataConnectorSourceType, string>> = {
  [DataConnectorSource.GOOGLE_ADS]: 'google_ads',
  [DataConnectorSource.GA4]: 'google_analytics',
  [DataConnectorSource.FACEBOOK]: 'facebook',
  [DataConnectorSource.BING]: 'bing'
};

export interface CheckAuthParams {
  company: string;
  source: DataConnectorSourceType;
  account_id: string;
  secret_path: string;
  mcc_id?: string;
}

const AUTH_CHECK_DEBOUNCE_MS = 500;

// ModalEditor re-runs checkIsValid on every keystroke, and checkAuth hits a live ad-platform auth
// endpoint.
export const debounceAuthCheck = (
  checkAuth: (params: CheckAuthParams) => Promise<boolean>
): ((params: CheckAuthParams) => Promise<boolean>) =>
  memoizeLastAsync(debounceAsync(checkAuth, AUTH_CHECK_DEBOUNCE_MS));

/**
 * Validates a Data Connector row before it's added. The duplicate check compares against every
 * currently-loaded row on the backend's own collision key (company + source + account_id), so
 * re-saving an existing row unchanged also flags as a self-duplicate.
 */
export const checkDataConnectorRowIsValid = async (
  data: Partial<DataConnectorRow>,
  existingRows: DataConnectorRow[] | undefined,
  checkAuth: (params: CheckAuthParams) => Promise<boolean>,
  handleInvalidMessage?: (message: string) => void
): Promise<boolean> => {
  const { company, source, account_id: accountId, secret_path: secretPath, mcc_id: mccId } = data;

  const missingFields: string[] = [];
  if (!company) {
    missingFields.push("'Company'");
  }
  if (!source) {
    missingFields.push("'Source'");
  }
  if (!accountId) {
    missingFields.push("'Account Id'");
  }
  if (!secretPath) {
    missingFields.push("'Auth Account'");
  }
  if (source === DataConnectorSource.GOOGLE_ADS && !mccId) {
    missingFields.push("'MCC Id'");
  }

  // The four `!x` checks below let TypeScript narrow company/source/accountId/secretPath to
  // defined strings for the rest of the function.
  if (!company || !source || !accountId || !secretPath || missingFields.length > 0) {
    handleInvalidMessage?.(
      `Please make sure ${missingFields.join(
        ', '
      )} for each row are set for new rows. MCC Id is only required when source is Google Ads.`
    );
    return false;
  }

  const requiredSecretSubstring = SOURCE_SECRET_SUBSTRING[source];
  if (requiredSecretSubstring && !secretPath.includes(requiredSecretSubstring)) {
    handleInvalidMessage?.('Source and Auth Account must match.');
    return false;
  }

  if (company !== company.toLowerCase()) {
    handleInvalidMessage?.('Company must be all lowercase.');
    return false;
  }

  // Matches the backend's own collision key exactly (company + source + account_id --
  // putMobiusDataConnectorJob's own duplicate check doesn't consider secret_path either), so a
  // row that passes this check won't silently fail to insert server-side.
  const duplicateRow = existingRows?.find(
    row => row.company === company && row.source === source && row.account_id === accountId
  );
  if (duplicateRow) {
    handleInvalidMessage?.(
      `Found duplicate row in mobius_data_connectors: ${duplicateRow.company}, ${duplicateRow.source}, ${duplicateRow.account_id}.`
    );
    return false;
  }

  const authCheck = await checkAuth({
    company,
    source,
    account_id: accountId,
    secret_path: secretPath,
    mcc_id: mccId
  });
  if (!authCheck) {
    handleInvalidMessage?.(`Auth failed for ${company}, ${source}, ${accountId}, ${secretPath}.`);
    return false;
  }

  return true;
};
